import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Breadcrumb, Input, Table, Space, Popconfirm, Col, Row } from "antd";
import { getRequest, postRequest } from "../../../axios";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import SearchIcon from "../../../image/SearchIcon.svg";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import "antd/dist/antd.min.css";
import "../../../styles/Main.scss";

const { Search } = Input;

const ArchivedPerformaInvoiceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archivedPerformaInvoiceList, setArchivedPerformaInvoiceList] =
    useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);

  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  useEffect(() => {
    const getPerformaInvoiceArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("performa-invoice-archive/", state);
        if (apiResponse.status) {
          setArchivedPerformaInvoiceList(apiResponse.data.results);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg("Error in archived performa invoice list");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getPerformaInvoiceArchiveList();
  }, [state, reload]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "company" && sortOrder === "ascend") {
      sortingEnumKey = "company";
    }
    if (sortField === "company" && sortOrder === "descend") {
      sortingEnumKey = "-company";
    }
    if (sortField === "subscription" && sortOrder === "ascend") {
      sortingEnumKey = "subscription";
    }
    if (sortField === "subscription" && sortOrder === "descend") {
      sortingEnumKey = "-subscription";
    }

    if (sortField === "payment_mode" && sortOrder === "ascend") {
      sortingEnumKey = "payment_mode";
    }
    if (sortField === "payment_mode" && sortOrder === "descend") {
      sortingEnumKey = "-payment_mode";
    }

    if (sortField === "active" && sortOrder === "ascend") {
      sortingEnumKey = "active";
    }
    if (sortField === "active" && sortOrder === "descend") {
      sortingEnumKey = "-active";
    }

    if (sortField === "start_date" && sortOrder === "ascend") {
      sortingEnumKey = "start_date";
    }
    if (sortField === "start_date" && sortOrder === "descend") {
      sortingEnumKey = "-start_date";
    }

    if (sortField === "end_date" && sortOrder === "ascend") {
      sortingEnumKey = "end_date";
    }
    if (sortField === "end_date" && sortOrder === "descend") {
      sortingEnumKey = "-end_date";
    }

    return sortingEnumKey;
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("performa-invoice-archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg("Performa Invoice restore successfully");
        setReload(!reload);
        navigate("/performa-invoice");
        setSelectedRow([]);
      } else {
        ErrorNotificationMsg("Error in restore Performa Invoice");
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: true,
    },
    {
      title: "Client",
      dataIndex: "business_name",
      key: "business_name",
      sorter: true,
    },
    {
      title: "Subscription",
      dataIndex: "subscription_name",
      key: "subscription_name",
      sorter: true,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      sorter: true,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      sorter: true,
    },
    {
      title: "Currency",
      dataIndex: "currency_name",
      key: "currency_name",
      sorter: true,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      sorter: true,
    },
    {
      title: "Sell Price",
      dataIndex: "sell_price",
      key: "sell_price",
      sorter: true,
    },
    {
      title: "GST Rate",
      dataIndex: "gst_rate",
      key: "gst_rate",
      sorter: true,
    },
    {
      title: "ACTION",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("pop_restore_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore className="ArchiverestorImg" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{t("Archived Performa Invoice List")}</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/performa-invoice">{t("performaInvoiceList")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5">
        <div className="PR-body">
          <Row className="tblheader">
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-heading">
            <h1>{t("Archived Performa Invoice List")}</h1>
          </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Col> */}
          </Row>

          <Col>
            <Table
              scroll={{
                x: 2000,
              }}
              rowSelection={rowSelection}
              rowKey={(record) => record.id}
              columns={columns}
              dataSource={archivedPerformaInvoiceList}
              loading={loading}
              pagination={{
                defaultCurrent: state.page,
                defaultPageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (totalCount) => `Total ${totalCount} items`,
              }}
              onChange={handleTableChange}
            />
          </Col>
        </div>
      </section>
    </>
  );
};

export default ArchivedPerformaInvoiceList;
